import type { MouseEvent, ReactNode } from 'react';
import { X } from 'lucide-react';

export interface ChipProps {
  label: string;
  color?: 'primary' | 'secondary' | 'neutral';
  small?: boolean;
  /** Transparent background with a colored border instead of a tint fill. */
  outline?: boolean;
  icon?: ReactNode;
  /**
   * When provided the chip renders as a <button> (or, with `onRemove` too, a
   * pill holding two sibling buttons).
   */
  onClick?: () => void;
  /** When provided an X remove affordance is appended. */
  onRemove?: () => void;
  className?: string;
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const colorClasses: Record<
  NonNullable<ChipProps['color']>,
  { filled: string; outline: string; hover: string }
> = {
  primary: {
    filled: 'bg-primary/10 text-primary-strong',
    outline: 'border border-primary/40 text-primary-strong',
    hover: 'hover:bg-primary/20',
  },
  secondary: {
    filled: 'bg-secondary/10 text-secondary',
    outline: 'border border-secondary/40 text-secondary',
    hover: 'hover:bg-secondary/20',
  },
  neutral: {
    filled: 'bg-line-light text-ink-secondary',
    outline: 'border border-line text-ink-secondary',
    hover: 'hover:bg-line',
  },
};

/** Rounded pill chip — filter labels, categories, tags. */
export function Chip({
  label,
  color = 'primary',
  small = false,
  outline = false,
  icon,
  onClick,
  onRemove,
  className,
}: ChipProps) {
  const palette = colorClasses[color];

  const classes = cn(
    'inline-flex items-center rounded-full font-semibold transition-all duration-200',
    small ? 'gap-1 px-2 py-0.5 text-xs' : 'gap-1.5 px-3 py-1.5 text-sm',
    outline ? palette.outline : palette.filled,
    onClick && cn('cursor-pointer', palette.hover),
    className,
  );

  const removeIconSize = small ? 12 : 14;
  const removeClasses =
    '-mr-0.5 inline-flex shrink-0 items-center justify-center rounded-full p-0.5 opacity-70 transition-all duration-200 hover:opacity-100';

  const handleRemoveClick = (e: MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const removeNode = onRemove ? (
    <button
      type="button"
      aria-label={`Remove ${label}`}
      onClick={handleRemoveClick}
      // Above the label button's stretched overlay (clickable + removable chip).
      className={cn(removeClasses, onClick && 'relative z-[1]')}
    >
      <X size={removeIconSize} aria-hidden="true" />
    </button>
  ) : null;

  const labelNodes = (
    <>
      {icon && <span className="inline-flex shrink-0 items-center">{icon}</span>}
      <span className="truncate">{label}</span>
    </>
  );

  // Clickable AND removable: two sibling buttons inside the pill. The remove
  // control used to be a role="button" span nested in the chip's <button> —
  // interactive content inside a button is invalid HTML, and screen readers
  // folded it into the outer button's name.
  //
  // The label button's ::after is stretched over the whole (relative) pill so
  // the padding and the gap before the X still open the chip, exactly as when
  // the pill itself was the button; the X is positioned above that overlay. The
  // focus ring moves onto the overlay too, so it outlines the pill as before.
  if (onClick && onRemove) {
    return (
      <span className={cn('relative', classes)}>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'inline-flex min-w-0 items-center rounded-full',
            small ? 'gap-1' : 'gap-1.5',
            'after:absolute after:inset-0 after:rounded-full',
            'focus-visible:ring-0 focus-visible:ring-offset-0',
            'focus-visible:after:ring-2 focus-visible:after:ring-primary/60 focus-visible:after:ring-offset-2 focus-visible:after:ring-offset-background',
          )}
        >
          {labelNodes}
        </button>
        {removeNode}
      </span>
    );
  }

  const content = (
    <>
      {labelNodes}
      {removeNode}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    );
  }

  return <span className={classes}>{content}</span>;
}
